import ContentShell from "components/ContentShell";
import AccessDenied from "components/AccessDenied";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import useStyles from "styles/brand/brandEditor";
import { SimpleTextInput } from "components/FormTextInput";
import {
  getBettingMarkets,
  getDefaultMarginInfo,
  updateDefaultMargin,
} from "services/bettingMarketService";
import { generateOptions, calculateOverallRTP } from "utils/miscUtils";
import sessionHelper from "services/sessionHelper";
import { userRoles } from "utils/constants";

const BaseMarketEditor = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [baseMarkets, setBaseMarkets] = useState<{
    marginValue: Record<string, any>;
    rtpValue: Record<string, any>;
    marketAvailability: Record<
      string,
      { enabled: boolean; disabledSubMarkets?: string[]; lockingEnabled?: boolean }
    >;
    isMarginEditingAllowed: boolean;
    adminLock?: boolean;
  }>({
    marginValue: {},
    rtpValue: {},
    marketAvailability: {},
    isMarginEditingAllowed: false,
  });
  const [options, setOptions] = useState({
    bettingMarkets: [],
    bettingMarketOptions: [],
    selectedMarkets: [],
  });
  const [errors, setErrors] = useState({});

  // Use ref to store showSnackbar to avoid dependency issues
  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;

  // Calculate RTP using formula: RTP = 1/(1+Margin)*100
  const calculateRTP = (margin: any) => {
    if (!margin || margin <= 0) return "0.00";
    const rtp = (1 / (1 + margin / 100)) * 100;
    return rtp.toFixed(2);
  };

  // Derive market keys that are explicitly disabled via marketAvailability
  const getDisabledMarkets = (marketAvailability) =>
    Object.entries(marketAvailability ?? {})
      .filter(([, v]: [string, any]) => v?.enabled === false)
      .map(([k]) => k);

  // Get validation status for margin data
  const getValidationStatus = (marginData, hasError) => {
    // If there's a validation error, return the error message
    if (hasError) return hasError;

    // If margin data is not set or empty, return NA
    if (
      !marginData ||
      marginData.value === "" ||
      marginData.value === null ||
      marginData.value === undefined
    ) {
      return "NA";
    }

    // If margin value exists and is within range, return true
    const min = marginData.min || 0;
    const max = marginData.max || 100;
    const value = marginData.value;

    if (value >= min && value <= max) {
      return "true";
    }

    // If value is outside range, return false
    return "false";
  };

  const submit = async () => {
    try {
      setLoading(true);

      // Check for any existing validation errors only if adminLock is enabled
      if (baseMarkets.adminLock && Object.keys(errors).length > 0) {
        throw new Error("Please fix validation errors before saving");
      }

      // Validate margin values are within min/max range only if adminLock is enabled
      if (baseMarkets.adminLock) {
        for (const [market, marginData] of Object.entries(
          baseMarkets.marginValue
        )) {
          // Skip validation if value is empty (allow saving with empty values)
          if (
            marginData.value === "" ||
            marginData.value === null ||
            marginData.value === undefined
          ) {
            continue;
          }

          if (
            marginData.value < marginData.min ||
            marginData.value > marginData.max
          ) {
            throw new Error(
              `${market} margin value must be equal and between ${marginData.min} and ${marginData.max}`
            );
          }
        }
      }

      // Prepare data in the new API format
      const operatorMargins = {};
      const extraData = {
        isMarginEditingAllowed: baseMarkets.isMarginEditingAllowed,
      };

      // Transform marginValue back to API format
      Object.entries(baseMarkets.marginValue).forEach(
        ([market, marginData]) => {
          // Handle empty values - send as 0 or empty string based on your API expectations
          operatorMargins[market] =
            marginData.value === "" ? 0 : marginData.value;
          extraData[market] = {
            min: marginData.min || 1, // Default min if empty
            max: marginData.max || 100, // Default max if empty
          };
        }
      );

      (operatorMargins as any).EXTRA_DATA = extraData;

      const dataToSubmit = {
        operatorMargins,
        operatorRtp: baseMarkets.rtpValue,
        ...(Object.keys(baseMarkets.marketAvailability || {}).length > 0
          ? { marketAvailability: baseMarkets.marketAvailability }
          : {}),
      };

      let response = await updateDefaultMargin(dataToSubmit);
      showSnackbar(response.message, "success");
      setLoading(false);
      navigate((configData.routes.operators as any).baseMarkets);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const onMarketChange = async (e, type, field = "value") => {
    const { name, value } = e?.target;

    // Handle empty string differently from 0
    let processedValue;
    if (value === "" || value === null || value === undefined) {
      processedValue = ""; // Keep as empty string for better UX
    } else {
      processedValue = Number(value);
    }

    if (type === "marginValue") {
      // Update margin value, min, or max
      const currentMarginData = baseMarkets.marginValue[name] || {};
      const newMarginValue = {
        ...baseMarkets.marginValue,
        [name]: {
          ...currentMarginData,
          [field]: processedValue,
        },
      };

      // Validate if updating margin value
      if (field === "value") {
        const min = currentMarginData.min || 0;
        const max = currentMarginData.max || 100;

        // Only validate if value is not empty
        if (
          processedValue !== "" &&
          (processedValue < min || processedValue > max)
        ) {
          setErrors((prev) => ({
            ...prev,
            [name]: `Margin value must be between ${min} and ${max}`,
          }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[name];
            return newErrors;
          });
        }

        // Calculate new RTP for this market only if value is valid and not empty
        if (
          processedValue !== "" &&
          processedValue >= min &&
          processedValue <= max
        ) {
          const newRtp = calculateRTP(processedValue);
          const newRtpValue = {
            ...baseMarkets.rtpValue,
            [name]: newRtp,
          };

          // Calculate overall RTP
          const overallRtp = calculateOverallRTP(
            newRtpValue,
            getDisabledMarkets(baseMarkets.marketAvailability),
          );
          (newRtpValue as any).OVER_ALL = overallRtp;

          setBaseMarkets((state) => ({
            ...state,
            marginValue: newMarginValue,
            rtpValue: newRtpValue,
          }));
        } else {
          // Update only margin value without RTP calculation if invalid or empty
          setBaseMarkets((state) => ({
            ...state,
            marginValue: newMarginValue,
          }));
        }
      } else {
        // For min/max updates, validate current margin value against new range
        const currentValue = currentMarginData.value || 0;
        const newMin =
          field === "min"
            ? processedValue === ""
              ? 0
              : processedValue
            : currentMarginData.min;
        const newMax =
          field === "max"
            ? processedValue === ""
              ? 0
              : processedValue
            : currentMarginData.max;

        // Validate min is not greater than max (only if both are not empty)
        if (newMin !== "" && newMax !== "" && newMin > newMax) {
          setErrors((prev) => ({
            ...prev,
            [name]: `Minimum value (${newMin}) cannot be greater than maximum value (${newMax})`,
          }));
          return; // Don't update state if validation fails
        }

        if (
          currentValue !== "" &&
          newMin !== "" &&
          newMax !== "" &&
          (currentValue < newMin || currentValue > newMax)
        ) {
          setErrors((prev) => ({
            ...prev,
            [name]: `Current margin value ${currentValue} is outside new range ${newMin}-${newMax}`,
          }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[name];
            return newErrors;
          });
        }

        setBaseMarkets((state) => ({
          ...state,
          marginValue: newMarginValue,
        }));
      }
    }

    if (type === "enabled") {
      const currentEntry = baseMarkets.marketAvailability[name] || {};
      const newMarketAvailability = {
        ...baseMarkets.marketAvailability,
        [name]: { ...currentEntry, enabled: e.target.checked },
      };

      const newRtpValue = { ...baseMarkets.rtpValue };
      const overallRtp = calculateOverallRTP(
        newRtpValue,
        getDisabledMarkets(newMarketAvailability),
      );
      (newRtpValue as any).OVER_ALL = overallRtp;

      setBaseMarkets((state) => ({
        ...state,
        marketAvailability: newMarketAvailability,
        rtpValue: newRtpValue,
      }));
    }

    if (type === "lockingEnabled") {
      const currentEntry = baseMarkets.marketAvailability[name] || {};
      const newMarketAvailability = {
        ...baseMarkets.marketAvailability,
        [name]: { ...currentEntry, lockingEnabled: e.target.checked },
      };

      setBaseMarkets((state) => ({
        ...state,
        marketAvailability: newMarketAvailability,
      }));
    }
  };

  const onAdminLockChange = (e: any) => {
    setBaseMarkets((state) => ({
      ...state,
      adminLock: e.target.checked,
    }));
  };

  const fetchBettingMarkets = useCallback(async () => {
    try {
      const [response, defaultMargin] = await Promise.all([
        getBettingMarkets(),
        getDefaultMarginInfo(),
      ]);

      const _options = generateOptions({
        items: response?.data?.markets,
        id: "id",
        label: "label",
      });

      // Handle new API response structure
      const marginData = defaultMargin.data || {};
      const { operatorMargins = {}, operatorRtp = {}, marketAvailability = {} } = marginData;

      // Check if we have any data or if this is an empty response
      const hasExistingData = Object.keys(operatorMargins).length > 0;

      // Transform operatorMargins to the format expected by the component
      const transformedMarginValue = {};
      const calculatedRtpValue = {};

      if (hasExistingData) {
        // Process existing data from API
        Object.keys(operatorMargins).forEach((market) => {
          if (market !== "EXTRA_DATA") {
            const marginValue = operatorMargins[market];
            const extraData = operatorMargins.EXTRA_DATA?.[market] || {
              min: 1,
              max: 100,
            };

            transformedMarginValue[market] = {
              value: marginValue,
              min: extraData.min,
              max: extraData.max,
            };
          }
        });

        // Use existing RTP values
        Object.assign(calculatedRtpValue, operatorRtp);
      } else {
        // No existing data - create default structure for all markets
        response?.data?.markets?.forEach((market) => {
          transformedMarginValue[market] = {
            value: "", // Empty value to allow user input
            min: 1, // Default min value
            max: 100, // Default max value
          };
          calculatedRtpValue[market] = "0.00"; // Default RTP
        });

        // Set default overall RTP
        (calculatedRtpValue as any).OVER_ALL = "0.00";
      }

      setBaseMarkets({
        marginValue: transformedMarginValue,
        rtpValue: calculatedRtpValue,
        marketAvailability,
        isMarginEditingAllowed: false,
        adminLock: hasExistingData
          ? operatorMargins.EXTRA_DATA?.isMarginEditingAllowed || false
          : false,
      });

      setOptions((state) => ({
        ...state,
        bettingMarkets: [...(response?.data?.markets || []), "OVER_ALL"],
        bettingMarketOptions: _options,
      }));
    } catch (error) {
      showSnackbarRef.current(error.message, "error");
    }
  }, []); // Safe to have empty dependency array now

  useEffect(() => {
    fetchBettingMarkets();
  }, [fetchBettingMarkets]);

  // Check if user has permission to access this page
  const allowedRoles = [
    userRoles.SUPER_ADMIN_DELETE,
    userRoles.SUPER_ADMIN,
    userRoles.ADMIN,
  ];
  if (!allowedRoles.includes(sessionHelper.role)) {
    return <AccessDenied />;
  }

  // Check if user can edit (only SUPER_ADMIN_DELETE)
  const canEdit = sessionHelper.role === userRoles.SUPER_ADMIN_DELETE;

  return (
    <ContentShell
      title={`Edit Market Margins`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button
            type="submit"
            variant="contained"
            onClick={submit}
            disabled={!canEdit}
          >
            Save
          </Button>
        </Box>
      }
    >
      <Box className={styles.container}>
        {/* Admin Lock Toggle */}
        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
            padding: 12,
            border: "1px solid #e0e0e0",
            borderRadius: 4,
          }}
        >
          <Typography variant="h6">Market Margins Lock</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={baseMarkets.adminLock}
                onChange={onAdminLockChange}
                disabled={!canEdit}
                color="primary"
              />
            }
            label={canEdit ? "Enable Editing" : "Read Only Mode"}
          />
        </Box>

        {/* Overall RTP Display */}
        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
            padding: 16,
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
            borderRadius: 8,
            border: "1px solid #e0e0e0",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              style={{ fontWeight: "bold", marginBottom: 4 }}
            >
              Overall Return to Player
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Real-time calculation based on all market RTPs
            </Typography>
          </Box>
          <Box style={{ textAlign: "right" }}>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginBottom: 4 }}
            >
              Overall RTP
            </Typography>
            <Typography
              variant="h4"
              style={{
                fontWeight: "bold",
                color: "#1976d2",
                backgroundColor: "white",
                padding: "8px 16px",
                borderRadius: 4,
                border: "2px solid #1976d2",
              }}
            >
              {baseMarkets?.rtpValue?.OVER_ALL || "0.00"}%
            </Typography>
          </Box>
        </Box>

        {/* Compact Table Layout */}
        <TableContainer component={Paper} style={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: "bold", minWidth: 150 }}>
                  Market
                </TableCell>
                <TableCell
                  style={{ fontWeight: "bold", width: 120, color: "red" }}
                >
                  Min Margin (%)
                </TableCell>
                <TableCell
                  style={{ fontWeight: "bold", width: 120, color: "red" }}
                >
                  Max Margin (%)
                </TableCell>
                <TableCell style={{ fontWeight: "bold", width: 120 }}>
                  Margin (%)
                </TableCell>
                <TableCell style={{ fontWeight: "bold", width: 100 }}>
                  RTP (%)
                </TableCell>
                <TableCell style={{ fontWeight: "bold", width: 200 }}>
                  Validation Status
                </TableCell>
                <TableCell style={{ fontWeight: "bold", width: 100 }}>
                  Enabled
                </TableCell>
                <TableCell style={{ fontWeight: "bold", width: 100 }}>
                  Auto-Lock
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {options?.bettingMarkets
                ?.filter((item) => item !== "OVER_ALL")
                ?.map((market) => {
                  const marginData = baseMarkets?.marginValue?.[market] || {};
                  const rtpValue = baseMarkets?.rtpValue?.[market];
                  const hasError = errors[market];

                  return (
                    <TableRow
                      key={market}
                      style={{
                        backgroundColor: hasError ? "#ffebee" : "inherit",
                      }}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: "bold" }}
                        >
                          {market.replace(/_/g, " ")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <SimpleTextInput
                          type="number"
                          name={market}
                          value={
                            marginData.min === "" ? "" : marginData.min || ""
                          }
                          onChange={(e) =>
                            onMarketChange(e, "marginValue", "min")
                          }
                          disabled={!baseMarkets.adminLock || !canEdit}
                          inputProps={{ step: 0.1, min: 0 }}
                          size="small"
                          style={{ width: "80px" }}
                          placeholder="Min"
                        />
                      </TableCell>
                      <TableCell>
                        <SimpleTextInput
                          type="number"
                          name={market}
                          value={
                            marginData.max === "" ? "" : marginData.max || ""
                          }
                          onChange={(e) =>
                            onMarketChange(e, "marginValue", "max")
                          }
                          disabled={!baseMarkets.adminLock || !canEdit}
                          inputProps={{ step: 0.1, min: 0 }}
                          size="small"
                          style={{ width: "80px" }}
                          placeholder="Max"
                        />
                      </TableCell>
                      <TableCell>
                        <SimpleTextInput
                          type="number"
                          name={market}
                          value={
                            marginData.value === ""
                              ? ""
                              : marginData.value || ""
                          }
                          onChange={(e) =>
                            onMarketChange(e, "marginValue", "value")
                          }
                          disabled={!baseMarkets.adminLock || !canEdit}
                          inputProps={{
                            min: marginData.min || 0,
                            max: marginData.max || 100,
                            step: 0.1,
                          }}
                          error={!!hasError}
                          size="small"
                          style={{ width: "100px" }}
                          placeholder="Margin"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: "bold", color: "#4caf50" }}
                        >
                          {rtpValue || "0.00"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const validationStatus = getValidationStatus(
                            marginData,
                            hasError
                          );

                          // Determine color based on status type
                          let statusColor;
                          if (validationStatus === "true") {
                            statusColor = "#4caf50"; // Green for valid
                          } else if (validationStatus === "NA") {
                            statusColor = "#9e9e9e"; // Gray for no data
                          } else if (validationStatus === "false") {
                            statusColor = "#f44336"; // Red for false
                          } else {
                            // Error message case
                            statusColor = "#f44336"; // Red for error messages
                          }

                          return (
                            <Typography
                              variant="caption"
                              style={{
                                fontSize: "11px",
                                color: statusColor,
                                fontWeight: "bold",
                              }}
                            >
                              {validationStatus}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={baseMarkets.marketAvailability?.[market]?.enabled ?? true}
                          onChange={(e) =>
                            onMarketChange(
                              { target: { name: market, checked: e.target.checked } } as any,
                              "enabled",
                            )
                          }
                          disabled={!baseMarkets.adminLock || !canEdit}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={baseMarkets.marketAvailability?.[market]?.lockingEnabled ?? true}
                          onChange={(e) =>
                            onMarketChange(
                              { target: { name: market, checked: e.target.checked } } as any,
                              "lockingEnabled",
                            )
                          }
                          disabled={!baseMarkets.adminLock || !canEdit}
                          color="primary"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </ContentShell>
  );
};

export default BaseMarketEditor;
